import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { postApi } from "../../services/api/postApi";
import { useUserContext } from "../../context/UserContext";
import FeedPost from "./FeedPost";
import PostSkeleton from "./PostSkeleton";
import FeedSidebar from "./FeedSidebar";
import { FiArrowLeft, FiHome, FiLogIn, FiAlertCircle } from "react-icons/fi";
import "./PostDetailPage.css";

const PostDetailPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { user } = useUserContext();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPostData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch main post
        const postRes = await postApi.getPost(postId);

        if (postRes.data.success && postRes.data.post) {
          setPost(postRes.data.post);

          // Fetch related posts
          try {
            const relatedRes = await postApi.getRelatedPosts(postId, 5);
            if (relatedRes.data.success) {
              setRelatedPosts(relatedRes.data.posts || []);
            }
          } catch (relatedErr) {
            // Related posts not available
            setRelatedPosts([]);
          }
        } else {
          setError("Post not found");
        }
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load post");
      } finally {
        setLoading(false);
      }
    };

    if (postId) {
      loadPostData();
    }
  }, [postId]);

  const handlePostUpdate = (postId, updates) => {
    setPost((prev) => (prev?._id === postId ? { ...prev, ...updates } : prev));
  };

  const handlePostDelete = (deletedPostId) => {
    navigate("/feed");
  };

  // Handle follow status change in single post view
  const handleFollowChange = (authorId, isFollowing) => {
    setPost((prev) =>
      prev?.author?._id === authorId
        ? { ...prev, isFollowingAuthor: isFollowing }
        : prev
    );
    // Also update related posts if same author
    setRelatedPosts((prev) =>
      prev.map((p) =>
        p.author?._id === authorId
          ? { ...p, isFollowingAuthor: isFollowing }
          : p
      )
    );
  };

  if (!user) {
    return (
      <div className="cbx-post-detail-page">
        <div className="cbx-pdp-container">
          <motion.div
            className="cbx-pdp-auth-required"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="cbx-pdp-auth-icon">
              <FiLogIn />
            </div>
            <h2>Please log in to view this post</h2>
            <p>Sign in to access all posts and connect with the community</p>
            <Link to="/" className="cbx-pdp-login-btn">
              <FiLogIn />
              Go to Login
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="cbx-post-detail-page">
      <div className="cbx-pdp-main">
        {/* Header */}
        <motion.div
          className="cbx-pdp-header"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <button className="cbx-pdp-back-btn" onClick={() => navigate(-1)}>
            <FiArrowLeft /> Back
          </button>
          <Link to="/feed" className="cbx-pdp-home-btn">
            <FiHome /> Feed
          </Link>
        </motion.div>

        {/* Main Content */}
        <div className="cbx-pdp-content">
          <AnimatePresence mode="wait">
            {loading ? (
              <motion.div
                className="cbx-pdp-loading"
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <PostSkeleton />
              </motion.div>
            ) : error ? (
              <motion.div
                className="cbx-pdp-error"
                key="error"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                <div className="cbx-pdp-error-icon">
                  <FiAlertCircle />
                </div>
                <h2>{error}</h2>
                <p>
                  The post you're looking for might have been deleted or doesn't
                  exist.
                </p>
                <Link to="/feed" className="cbx-pdp-home-link">
                  <FiHome />
                  Go back to Feed
                </Link>
              </motion.div>
            ) : post ? (
              <motion.div
                key="content"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {/* Main Post */}
                <div className="cbx-pdp-main-post">
                  <FeedPost
                    post={post}
                    onUpdate={handlePostUpdate}
                    onDelete={handlePostDelete}
                    onFollowChange={handleFollowChange}
                    expanded={true}
                  />
                </div>

                {/* Related Posts Section */}
                {relatedPosts.length > 0 && (
                  <motion.div
                    className="cbx-pdp-related-section"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                  >
                    <h3 className="cbx-pdp-related-title">Related Posts</h3>
                    <div className="cbx-pdp-related-list">
                      {relatedPosts.map((relPost, index) => {
                        const authorName = relPost.author?.name || "User";
                        const initials = authorName
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .substring(0, 2)
                          .toUpperCase();

                        return (
                          <motion.div
                            key={relPost._id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 * index }}
                          >
                            <Link
                              to={`/posts/${relPost._id}`}
                              className="cbx-pdp-related-card"
                            >
                              <div className="cbx-pdp-related-author">
                                {relPost.author?.profilePhotoUrl ? (
                                  <img
                                    src={relPost.author.profilePhotoUrl}
                                    alt={authorName}
                                    className="cbx-pdp-related-avatar"
                                  />
                                ) : (
                                  <div className="cbx-pdp-related-avatar cbx-pdp-avatar-initials">
                                    {initials}
                                  </div>
                                )}
                                <span>{authorName}</span>
                              </div>
                              <p className="cbx-pdp-related-content">
                                {relPost.content?.substring(0, 120)}
                                {relPost.content?.length > 120 ? "..." : ""}
                              </p>
                              {relPost.tags?.length > 0 && (
                                <div className="cbx-pdp-related-tags">
                                  {relPost.tags.slice(0, 3).map((tag) => (
                                    <span
                                      key={tag}
                                      className="cbx-pdp-related-tag"
                                    >
                                      #{tag}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </Link>
                          </motion.div>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </div>

      {/* Right Sidebar - same as main feed */}
      <FeedSidebar />
    </div>
  );
};

export default PostDetailPage;
